import type { HandwrittenNoteTypes } from '@mathflat/handwritten-note'
import { subMonths } from 'date-fns'
import type { DBSchema, IDBPDatabase, IDBPObjectStore, StoreNames } from 'idb'
import { openDB } from 'idb'

type NoteData = HandwrittenNoteTypes.NoteData

const NOTE_VALID_MONTHS = 6

// ★ IndexedDB schema — 본 service / integration write (handleStudentMainLogic) /
//   인라인은 import 불가 → window.__INTEGRATION_CONSTANTS__.INDEXED_DB 로 빌드 시점 주입.
export const HANDWRITTEN_NOTE_DB_NAME = 'handwritten-note'
export const STUDENT_WORKSHEET_NOTE_STORE = 'studentWorksheetNote'
export const STUDENT_WORKSHEET_NOTE_KEY_PATH: readonly string[] = [
  'studentWorksheetId',
  'worksheetProblemId',
]

interface StudentWorksheetNoteKey {
  studentWorksheetId: number
  worksheetProblemId: number
}

// 서술형 슬롯 필기는 (swId, wpId, tag) 3축 키가 필요한데, 기존 DB의 버전을 올리면
// 통합 마이그레이션 스크립트(handleStudentMainLogic)의 open(name, 1)이 VersionError로
// 조용히 실패하므로 별도 DB로 격리한다
export const ESSAY_SLOT_NOTE_DB_NAME = 'essay-slot-note'
export const ESSAY_SLOT_NOTE_STORE = 'essaySlotNote'

export interface EssaySlotNoteKey extends StudentWorksheetNoteKey {
  /** 1-based 슬롯 번호 */
  tag: number
}

export interface EssaySlotNoteValue extends EssaySlotNoteKey {
  noteData: NoteData
  timestamp: number
}

export interface StudentWorksheetNoteValue extends StudentWorksheetNoteKey {
  noteData: NoteData
  timestamp: number
}

interface ObjectStore extends DBSchema {
  studentWorksheetNote: {
    key: number[]
    value: StudentWorksheetNoteValue
  }
}

type NoteStoreMode = 'readonly' | 'readwrite'

type NoteStore<Mode extends NoteStoreMode> = IDBPObjectStore<
  ObjectStore,
  StoreNames<ObjectStore>[],
  'studentWorksheetNote',
  Mode
>

// 연결 상실은 트랜잭션 생성(동기)에서만 오지 않는다. iOS WebView 가 IndexedDB 백엔드와의 연결을
// 끊었을 때의 UnknownError 는 요청·커밋에서 비동기로 올라오므로, 생성 시점만 감싸면 끊긴 커넥션이
// 그대로 남아 이후 모든 접근이 같은 에러로 실패한다. 그래서 연산 전체를 재시도 단위로 삼는다.
const CONNECTION_LOST_ERROR_NAMES = ['InvalidStateError', 'UnknownError']

// 연결이 끊겨도 필기 저장은 획마다 계속 시도된다. 매번 실패를 던지면 호출부가 그때마다 로그·토스트를
// 내보내 화면과 원격 수집을 도배한다(실측 최대 795회/세션). 재오픈으로도 복구되지 않으면 이 시간
// 동안은 접근 자체를 건너뛰고, 지나면 다시 시도해 연결이 살아났을 때 스스로 복구한다.
const CONNECTION_LOST_COOLDOWN_MS = 30_000

const isConnectionLostError = (err: unknown) =>
  err instanceof Error && CONNECTION_LOST_ERROR_NAMES.includes(err.name)

const getNoteKeyFromValue = (value: StudentWorksheetNoteValue) => {
  return {
    studentWorksheetId: value.studentWorksheetId,
    worksheetProblemId: value.worksheetProblemId,
  }
}

export class IndexedDbService {
  private db?: IDBPDatabase<ObjectStore>
  private connectionLostAt = 0

  // indexedDB 전역이 없는 WebView/제한 환경에서는 openDB 가 ReferenceError 를 던지므로,
  // 접근 전 가용성을 확인해 저장/조회를 조용히 스킵한다(그 환경은 애초에 영속화가 불가능).
  private _isIndexedDbAvailable(): boolean {
    return typeof indexedDB !== 'undefined' && indexedDB !== null
  }

  // 연결 상실 직후 쿨다운 동안은 indexedDB 가 없는 환경과 동일하게 취급한다 — 조회는 빈 값,
  // 저장·삭제는 no-op 이라 호출부가 이미 다루는 경로로 조용히 지나간다.
  private _isUsable(): boolean {
    if (!this._isIndexedDbAvailable()) return false
    return Date.now() - this.connectionLostAt >= CONNECTION_LOST_COOLDOWN_MS
  }

  private async _ensureConnection() {
    if (!this.db) {
      await this.open()
    }
    if (!this.db) {
      throw new Error('db not initialized')
    }
  }

  private async _runInStore<Mode extends NoteStoreMode, T>(
    mode: Mode,
    run: (store: NoteStore<Mode>) => Promise<T>,
  ): Promise<T> {
    for (let attempt = 0; ; attempt += 1) {
      try {
        // 재연결도 같은 방어 안에 둔다. 연결이 끊긴 뒤에는 재오픈이 가장 먼저·가장 확실하게 실패하는
        // 지점이라, 이 호출이 try 밖이면 실패가 catch 에 닿지 않아 쿨다운이 영영 걸리지 않는다.
        await this._ensureConnection()

        const tx = this.db!.transaction(STUDENT_WORKSHEET_NOTE_STORE, mode)
        const result = await run(tx.store as NoteStore<Mode>)
        await tx.done
        return result
      } catch (err) {
        // 전파는 새 예외를 만들지 않고 원래 실패를 거절된 프로미스로 돌려준다.
        //
        // 연결 문제가 아니면 개입하지 않는다. 삼키면 QuotaExceededError 같은 처리 대상이 묻힌다.
        if (!isConnectionLostError(err)) return Promise.reject(err)

        // 끊긴 커넥션을 버려 다음 _ensureConnection 이 새로 열게 한다.
        this.db = undefined

        if (attempt > 0) {
          // 재오픈으로도 복구되지 않았다. 이 실패만 호출부에 알리고 쿨다운으로 반복을 막는다.
          this.connectionLostAt = Date.now()
          return Promise.reject(err)
        }
      }
    }
  }

  async open() {
    if (this.db) return
    if (!this._isIndexedDbAvailable()) return
    const database = await openDB<ObjectStore>(HANDWRITTEN_NOTE_DB_NAME, 1, {
      upgrade(db) {
        db.createObjectStore(STUDENT_WORKSHEET_NOTE_STORE, {
          keyPath: [...STUDENT_WORKSHEET_NOTE_KEY_PATH],
        })
      },
      // 옛 연결이 이 버전 오픈을 막는 경우. reload 로 벗어나되 stale 참조를 먼저 비운다.
      blocked: () => {
        this.db = undefined
        window.location.reload()
      },
      // 이 연결이 미래 버전 오픈을 막는 경우(versionchange). 닫고 비운 뒤 reload.
      blocking: () => {
        this.db?.close()
        this.db = undefined
        window.location.reload()
      },
    })
    // iOS WebView 등에서 UA 가 연결을 강제로 닫으면(close 이벤트) stale 참조를 비워
    // 다음 접근 시 _ensureConnection 이 재오픈하도록 한다.
    // 뒤늦게 도착한 옛 연결의 close 가 이미 교체된 새 연결을 무효화하지 않도록 자기 연결일 때만 비운다.
    database.addEventListener('close', () => {
      if (this.db === database) this.db = undefined
    })
    this.db = database
  }

  private _getStudentWorksheetCompositKey(key: StudentWorksheetNoteKey) {
    return [key.studentWorksheetId, key.worksheetProblemId]
  }

  async getStudentWorksheetNote(key: StudentWorksheetNoteKey) {
    if (!this._isUsable()) return undefined
    return this._runInStore('readonly', (store) =>
      store.get(this._getStudentWorksheetCompositKey(key)),
    )
  }

  async getStudentWorksheetNotes(keys: StudentWorksheetNoteKey[]) {
    if (!this._isUsable()) return []
    const data = await this._runInStore('readonly', (store) =>
      Promise.all(keys.map((key) => store.get(this._getStudentWorksheetCompositKey(key)))),
    )
    return data.filter((item) => Boolean(item)) as StudentWorksheetNoteValue[]
  }

  async getAllStudentWorksheetNotes() {
    if (!this._isUsable()) return []
    return this._runInStore('readonly', (store) => store.getAll())
  }

  async putStudentWorksheetNote(value: StudentWorksheetNoteValue) {
    if (!this._isUsable()) return
    try {
      await this._runInStore('readwrite', (store) => store.put(value))
    } catch (err) {
      if (err instanceof Error && err.name === 'QuotaExceededError') {
        const expiredTimestamp = subMonths(new Date(), NOTE_VALID_MONTHS).getTime()
        const allNotes = await this.getAllStudentWorksheetNotes()

        const nonCurrentWorksheetNotes = allNotes.filter(
          (note) => note.studentWorksheetId !== value.studentWorksheetId,
        )

        let targetNotes = nonCurrentWorksheetNotes.filter(
          (note) => !note.timestamp || note.timestamp <= expiredTimestamp,
        )

        if (!targetNotes.length) {
          // 삭제할 노트 없으면 현재 풀이중인 학습지 필기 외에 전부 삭제
          targetNotes = [...nonCurrentWorksheetNotes]
        }

        if (targetNotes.length) {
          await this.deleteStudentWorksheetNotes(targetNotes.map(getNoteKeyFromValue))
          // Retry after cleanup
          await this._runInStore('readwrite', (store) => store.put(value))
          return
        }
      }
      throw err
    }
  }

  async deleteStudentWorksheetNotes(keys: StudentWorksheetNoteKey[]) {
    if (!this._isUsable()) return
    await this._runInStore('readwrite', (store) =>
      Promise.all(keys.map((key) => store.delete(this._getStudentWorksheetCompositKey(key)))),
    )
  }

  close() {
    if (this.db) {
      this.db.close()
      this.db = undefined
    }
  }
}

interface EssaySlotNoteObjectStore extends DBSchema {
  essaySlotNote: {
    key: number[]
    value: EssaySlotNoteValue
  }
}

/**
 * 서술형 슬롯 필기 로컬 큐 — 행 존재 = 서버 미업로드분.
 * 업로드 성공 시 삭제하며, 남은 행은 재진입 시 복원·재업로드 대상이 된다.
 */
export class EssaySlotNoteDbService {
  private dbPromise?: Promise<IDBPDatabase<EssaySlotNoteObjectStore>>

  // promise 를 캐싱해야 병렬 첫 호출이 openDB 를 중복 실행해 커넥션을 누수하지 않는다
  private _ensureDb() {
    if (!this.dbPromise) {
      this.dbPromise = openDB<EssaySlotNoteObjectStore>(ESSAY_SLOT_NOTE_DB_NAME, 1, {
        upgrade(db) {
          db.createObjectStore(ESSAY_SLOT_NOTE_STORE, {
            keyPath: ['studentWorksheetId', 'worksheetProblemId', 'tag'],
          })
        },
      }).then((db) => {
        // 커넥션이 끊기면 다음 접근에서 다시 열도록 캐시를 비운다
        db.addEventListener('close', () => {
          this.dbPromise = undefined
        })
        return db
      })
      this.dbPromise.catch(() => {
        this.dbPromise = undefined
      })
    }
    return this.dbPromise
  }

  async getAllByStudentWorksheet(studentWorksheetId: number): Promise<EssaySlotNoteValue[]> {
    const db = await this._ensureDb()
    // 배열 키 비교상 [swId, ...] 는 모두 [swId] 이상 [swId+1] 미만 구간에 들어온다
    return db.getAll(
      ESSAY_SLOT_NOTE_STORE,
      IDBKeyRange.bound([studentWorksheetId], [studentWorksheetId + 1]),
    )
  }

  async put(value: EssaySlotNoteValue) {
    const db = await this._ensureDb()
    await db.put(ESSAY_SLOT_NOTE_STORE, value)
  }

  async delete(keys: EssaySlotNoteKey[]) {
    const db = await this._ensureDb()
    const tx = db.transaction(ESSAY_SLOT_NOTE_STORE, 'readwrite')
    await Promise.all(
      keys.map((key) => tx.store.delete([key.studentWorksheetId, key.worksheetProblemId, key.tag])),
    )
    await tx.done
  }
}
